import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
  AiAgentsNamespaceDeleteWebsiteSourceArgs,
  Mutation,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useShowConfirmationModal } from 'src/components/general/feedback/hooks/use-show-confirmation-modal';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import { useConfirmationModalSize } from 'src/components/pages/ai-agents/components/confirmation-modal-parts';
import { useAgentSourceDeleteDialog } from 'src/components/pages/ai-agents/knowledge-base-sources/hooks/use-agent-source-delete-dialog';
import { DELETE_WEBSITE_SOURCE_MUTATION } from 'src/components/pages/ai-agents/knowledge-base-sources/websites/queries';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

export const useDeleteWebsiteSource = ({
  onDeleted,
  aiAgentId,
}: {
  onDeleted: VoidFunction;
  aiAgentId?: string;
}) => {
  const { t } = useTranslation(['aiAgents']);
  const { id: companyId } = useCurrentInstitution();
  const showConfirmationModal = useShowConfirmationModal();
  const showNotification = useShowNotification();
  const handleGraphQLError = useHandleGraphQLError();
  const modalSize = useConfirmationModalSize();
  const agentSourceDeleteDialog = useAgentSourceDeleteDialog();

  const [deleteWebsite] = useMutation<
    Pick<Mutation, 'aiAgents'>,
    AiAgentsNamespaceDeleteWebsiteSourceArgs
  >(DELETE_WEBSITE_SOURCE_MUTATION, { onError: handleGraphQLError });

  return useCallback(
    (website: { id: string }) => {
      const onConfirm = async () => {
        const result = await deleteWebsite({
          variables: { input: { companyId, id: website.id } },
          update: (cache) => {
            cache.evict({
              id: cache.identify({
                id: website.id,
                __typename: 'AIAgentWebsiteSource',
              }),
            });
            cache.gc();
          },
        });

        if (result.data?.aiAgents.deleteWebsiteSource.success) {
          showNotification({
            type: 'success',
            title: t('aiAgents:knowledge.websites.delete.success'),
          });
          onDeleted();
        }
      };

      const modalProps = {
        testId: 'website-delete-modal',
        ...modalSize,
      };

      if (aiAgentId) {
        void agentSourceDeleteDialog({
          aiAgentId,
          sourceId: website.id,
          onConfirm: () => void onConfirm(),
        }).then(
          (dialog) => dialog && showConfirmationModal({ ...modalProps, ...dialog }),
        );

        return;
      }

      showConfirmationModal({
        ...modalProps,
        title: t('aiAgents:knowledge.websites.delete.title'),
        message: t('aiAgents:knowledge.websites.delete.message'),
        confirmButtonText: t('aiAgents:knowledge.websites.delete.confirm'),
        confirmButtonType: 'delete',
        onConfirm,
      });
    },
    [
      aiAgentId,
      agentSourceDeleteDialog,
      companyId,
      deleteWebsite,
      modalSize,
      onDeleted,
      showConfirmationModal,
      showNotification,
      t,
    ],
  );
};
